/**
 * Sterntaler Facets v2 — Presentation- und Update-Controller
 *
 * Overlay, Drawer und mobile Bottom Sheets werden beim Start in ein Portal
 * direkt unter <body> verschoben. Grund: position:fixed innerhalb des
 * horizontal scrollenden Chip-Containers wird von iOS/WebKit unzuverlässig
 * gerendert (Sheets/Overlay bleiben unsichtbar). Die Formular-Zuordnung der
 * Inputs bleibt über das form="StfForm"-Attribut erhalten.
 *
 * Zwei Betriebsmodi (automatisch erkannt über data-native am #StfRoot):
 * - Nativ (Search & Discovery): serialisiert die Filter-Inputs, holt die
 *   Section per Section Rendering API und tauscht Grid, Zähler und Facetten.
 * - Grid-App: collection-grid-app.js rendert Facetten und lädt das Grid;
 *   dieser Controller steuert Öffnen/Schließen und die Sortierung.
 */
(function () {
  'use strict';

  var DESKTOP_MQ = '(min-width: 990px)';
  var DEBOUNCE_MS = 250;

  // Geteilter Zustand — collection-grid-app.js liest ihn beim Re-Render.
  var state = (window.__stf = window.__stf || {
    openPanel: null,
    openAcc: null,
    drawerOpen: false,
  });

  var debounceTimer = null;
  var portal = null;
  var sheetHost = null;

  function root() {
    return document.getElementById('StfRoot');
  }

  function isNative() {
    var r = root();
    return Boolean(r && r.getAttribute('data-native') === 'true');
  }

  function overlay() {
    return document.getElementById('StfOverlay');
  }

  function drawer() {
    return document.getElementById('StfDrawer');
  }

  function isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  function lockScroll(lock) {
    document.documentElement.classList.toggle('stf-lock', Boolean(lock));
  }

  /* ------------------------------ Portal-Setup ---------------------------- */

  function ensurePortal() {
    if (portal && portal.isConnected) return portal;
    if (!root()) return null;

    portal = document.getElementById('StfPortal');
    if (!portal) {
      portal = document.createElement('div');
      portal.id = 'StfPortal';
      portal.className = 'stf stf-portal';
      document.body.appendChild(portal);
    }

    var ov = overlay();
    if (ov && ov.parentElement !== portal) portal.appendChild(ov);
    var d = drawer();
    if (d && d.parentElement !== portal) portal.appendChild(d);

    sheetHost = document.getElementById('StfSheetHost');
    if (!sheetHost) {
      sheetHost = document.createElement('div');
      sheetHost.id = 'StfSheetHost';
      // collection-grid-app-facets: damit die Change-/Reset-Delegation der
      // Grid-App auch für hierher verschobene Panels greift.
      sheetHost.className = 'collection-grid-app-facets';
      portal.appendChild(sheetHost);
    }
    return portal;
  }

  function clearSheetHost() {
    if (!sheetHost) return;
    while (sheetHost.firstChild) sheetHost.removeChild(sheetHost.firstChild);
  }

  /* ------------------------------ Overlay & Lock -------------------------- */

  function syncOverlay() {
    var ov = overlay();
    if (!ov) return;
    var needsOverlay = state.drawerOpen || (state.openPanel && !isDesktop());
    if (needsOverlay) {
      ov.hidden = false;
      requestAnimationFrame(function () {
        ov.classList.add('is-visible');
      });
    } else {
      ov.classList.remove('is-visible');
      ov.hidden = true;
    }
    lockScroll(needsOverlay);
  }

  /* ------------------------------ Panels ---------------------------------- */

  function facetByKey(key) {
    var r = root();
    if (!r || !key) return null;
    var facets = r.querySelectorAll('#StfChipsDyn .stf-facet[data-filter-key]');
    for (var i = 0; i < facets.length; i += 1) {
      if (facets[i].getAttribute('data-filter-key') === key) return facets[i];
    }
    return null;
  }

  function panelFor(facet) {
    var inHost = sheetHost ? sheetHost.querySelector('.stf-panel') : null;
    if (inHost) return inHost;
    return facet ? facet.querySelector('.stf-panel') : null;
  }

  function closePanel() {
    if (!state.openPanel) return;
    var facet = facetByKey(state.openPanel);
    var panel = panelFor(facet);
    if (panel) {
      panel.classList.remove('is-open');
      if (facet && panel.parentElement === sheetHost) facet.appendChild(panel);
    }
    clearSheetHost();
    if (facet) {
      facet.classList.remove('is-open', 'is-align-right');
      var chip = facet.querySelector('.stf-chip');
      if (chip) chip.setAttribute('aria-expanded', 'false');
    }
    state.openPanel = null;
    syncOverlay();
  }

  function presentPanel(facet) {
    ensurePortal();
    facet.classList.add('is-open');
    var chip = facet.querySelector('.stf-chip');
    if (chip) chip.setAttribute('aria-expanded', 'true');

    var panel = facet.querySelector('.stf-panel') || panelFor(facet);
    if (!panel) return;

    if (isDesktop()) {
      // Popover bleibt im Facet verankert
      if (panel.parentElement === sheetHost) facet.appendChild(panel);
      clearSheetHost();
      panel.classList.add('is-open');
      facet.classList.remove('is-align-right');
      var rect = panel.getBoundingClientRect();
      if (rect.right > window.innerWidth - 16) {
        facet.classList.add('is-align-right');
      }
    } else {
      // Bottom Sheet: ins Portal verschieben, dann animiert einblenden
      if (panel.parentElement !== sheetHost) {
        clearSheetHost();
        sheetHost.appendChild(panel);
      }
      void panel.offsetHeight; // Reflow, damit die Transition greift
      panel.classList.add('is-open');
    }
    syncOverlay();
  }

  function openPanel(facet) {
    var key = facet.getAttribute('data-filter-key');
    if (!key) return;
    if (state.openPanel === key) {
      closePanel();
      return;
    }
    closePanel();
    closeDrawer();
    state.openPanel = key;
    presentPanel(facet);
  }

  // Nach Re-Render/Breakpoint-Wechsel den offenen Zustand wiederherstellen
  function restoreOpenPanel() {
    if (!state.openPanel) {
      clearSheetHost();
      syncOverlay();
      return;
    }
    var facet = facetByKey(state.openPanel);
    if (!facet) {
      state.openPanel = null;
      clearSheetHost();
      syncOverlay();
      return;
    }
    // Nach einem Server-Swap hängt das frische Panel wieder im Facet;
    // ein evtl. veraltetes Panel im Host wird verworfen.
    var freshPanel = facet.querySelector('.stf-panel');
    if (freshPanel) clearSheetHost();
    presentPanel(facet);
  }

  /* ------------------------------ Drawer ----------------------------------- */

  function openDrawer() {
    closePanel();
    ensurePortal();
    var d = drawer();
    if (!d) return;
    state.drawerOpen = true;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    var allChip = document.getElementById('StfAllChip');
    if (allChip) allChip.setAttribute('aria-expanded', 'true');
    syncOverlay();
  }

  function closeDrawer() {
    if (!state.drawerOpen) return;
    var d = drawer();
    state.drawerOpen = false;
    if (d) {
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
    }
    var allChip = document.getElementById('StfAllChip');
    if (allChip) allChip.setAttribute('aria-expanded', 'false');
    syncOverlay();
  }

  function closeAll() {
    closePanel();
    closeDrawer();
  }

  /* ------------------------------- Native Mode ---------------------------- */

  function serializeForm() {
    var form = document.getElementById('StfForm');
    var params = new URLSearchParams();
    if (!form) return params;

    // form.elements enthält dank form="StfForm" auch die ins Portal
    // verschobenen Inputs (Sheets, Drawer).
    var seen = new Set();
    new FormData(form).forEach(function (value, key) {
      var v = String(value).trim();
      if (!v) return;
      var pairId = key + '\u0001' + v;
      if (seen.has(pairId)) return; // Zwillings-Inputs (Chip-Panel + Drawer) dedupen
      seen.add(pairId);
      params.append(key, v);
    });

    var sortSelect = document.querySelector('[data-stf-drawer-sort]');
    if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);

    return params;
  }

  function captureAccordionState() {
    var open = new Set();
    document.querySelectorAll('#StfDrawerBody details.stf-acc[data-filter-key]').forEach(function (acc) {
      if (acc.open) open.add(acc.getAttribute('data-filter-key'));
    });
    return open;
  }

  function restoreAccordionState(openKeys) {
    document.querySelectorAll('#StfDrawerBody details.stf-acc[data-filter-key]').forEach(function (acc) {
      acc.open = openKeys.has(acc.getAttribute('data-filter-key'));
    });
  }

  function swapFromDocument(doc) {
    var swapIds = ['ProductGridContainer', 'ProductCount', 'StfChipsDyn', 'StfDrawerBody', 'StfActivePills'];
    swapIds.forEach(function (id) {
      var source = doc.getElementById(id);
      var target = document.getElementById(id);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    var sourceBadge = doc.querySelector('[data-stf-all-badge]');
    var targetBadge = document.querySelector('[data-stf-all-badge]');
    if (sourceBadge && targetBadge) {
      targetBadge.textContent = sourceBadge.textContent;
      targetBadge.hidden = sourceBadge.hidden;
    }

    var count = document.getElementById('ProductCount');
    if (count) {
      var text = count.textContent.trim();
      document.querySelectorAll('[data-stf-count-btn]').forEach(function (el) {
        el.textContent = text + ' anzeigen';
      });
      document.querySelectorAll('[data-stf-count-mirror]').forEach(function (el) {
        el.textContent = text;
      });
    }
  }

  var latestNativeRequest = 0;

  function nativeRender(paramsString, push) {
    var r = root();
    if (!r) return;
    var sectionId = r.getAttribute('data-section-id');
    if (!sectionId) return;

    var grid = document.getElementById('ProductGridContainer');
    if (grid) grid.classList.add('stf-is-loading');

    var openAcc = captureAccordionState();
    var requestId = ++latestNativeRequest;
    var url = window.location.pathname + '?section_id=' + sectionId + (paramsString ? '&' + paramsString : '');

    fetch(url)
      .then(function (response) {
        return response.text();
      })
      .then(function (responseText) {
        if (requestId !== latestNativeRequest) return;
        var doc = new DOMParser().parseFromString(responseText, 'text/html');
        swapFromDocument(doc);
        restoreAccordionState(openAcc);
        restoreOpenPanel();
        updateScrollHint();

        var historyUrl = window.location.pathname + (paramsString ? '?' + paramsString : '');
        if (push) {
          window.history.pushState({ stfParams: paramsString }, '', historyUrl);
        } else {
          window.history.replaceState({ stfParams: paramsString }, '', historyUrl);
        }
      })
      .catch(function (err) {
        console.error('Sterntaler Facets v2:', err);
      })
      .finally(function () {
        if (requestId !== latestNativeRequest) return;
        var g = document.getElementById('ProductGridContainer');
        if (g) g.classList.remove('stf-is-loading');
      });
  }

  function scheduleNativeUpdate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      nativeRender(serializeForm().toString(), true);
    }, DEBOUNCE_MS);
  }

  /* ------------------------------ Grid-App Mode --------------------------- */

  function appReload(url) {
    window.history.replaceState({}, '', url.toString());
    var grid = document.getElementById('product-grid');
    if (grid) {
      grid.removeAttribute('data-seed');
      grid.setAttribute('data-page', '1');
    }
    document.dispatchEvent(
      new CustomEvent('facets:productGridUpdated', {
        detail: { searchParams: url.search.replace(/^\?/, '') },
      })
    );
  }

  /* ------------------------------- Sortierung ----------------------------- */

  function applySort(value) {
    var drawerSelect = document.querySelector('[data-stf-drawer-sort]');
    if (drawerSelect) drawerSelect.value = value;

    if (isNative()) {
      scheduleNativeUpdate();
    } else {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      appReload(url);
    }
  }

  function clearAllFilters() {
    if (isNative()) {
      var params = new URLSearchParams();
      var form = document.getElementById('StfForm');
      if (form) {
        // Suchbegriff (q, options[prefix]) erhalten, Filter verwerfen
        new FormData(form).forEach(function (value, key) {
          if (key === 'q' || key === 'options[prefix]') params.append(key, value);
        });
      }
      var sortSelect = document.querySelector('[data-stf-drawer-sort]');
      if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);
      nativeRender(params.toString(), true);
      return;
    }

    var url = new URL(window.location.href);
    var toDelete = [];
    url.searchParams.forEach(function (value, key) {
      if (key.indexOf('filter.') === 0) toDelete.push(key);
    });
    toDelete.forEach(function (key) {
      url.searchParams.delete(key);
    });
    appReload(url);
  }

  /* ------------------------------ Event-Delegation ------------------------ */

  document.addEventListener('click', function (event) {
    var r = root();
    if (!r) return;

    // Aktive Pills / Reset-Links (nativ): href enthält die Ziel-URL
    var removeLink = event.target.closest('[data-stf-remove]');
    if (removeLink) {
      event.preventDefault();
      var href = removeLink.getAttribute('href') || '';
      var queryIndex = href.indexOf('?');
      nativeRender(queryIndex === -1 ? '' : href.slice(queryIndex + 1), true);
      return;
    }

    var closeBtn = event.target.closest('[data-stf-close]');
    if (closeBtn) {
      closeAll();
      return;
    }

    if (event.target.closest('#StfOverlay')) {
      closeAll();
      return;
    }

    if (event.target.closest('#StfAllChip')) {
      state.drawerOpen ? closeDrawer() : openDrawer();
      return;
    }

    if (event.target.closest('#StfClearAll')) {
      clearAllFilters();
      return;
    }

    var toggle = event.target.closest('[data-stf-toggle]');
    if (toggle) {
      var facet = toggle.closest('.stf-facet');
      if (facet) openPanel(facet);
      return;
    }

    // Klick außerhalb schließt offene Popover/Sheets — Klicks im Panel
    // (auch im Portal) und im Drawer zählen als "innerhalb".
    if (
      state.openPanel &&
      !event.target.closest('.stf-facet') &&
      !event.target.closest('.stf-panel') &&
      !event.target.closest('#StfPortal')
    ) {
      closePanel();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAll();
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches) return;

    // Sortierung (Drawer-Select)
    if (target.matches('[data-stf-drawer-sort]')) {
      applySort(target.value);
      return;
    }

    // :has()-Fallback für ältere Browser
    if (target.matches('.stf-check input, .stf-swatch input')) {
      var wrap = target.closest('.stf-check, .stf-swatch');
      if (wrap) wrap.classList.toggle('is-checked', target.checked);
    }

    // Native Filter-Inputs (im Formular ODER via form-Attribut zugeordnet)
    var isStfInput =
      target.name &&
      target.name.indexOf('filter.') === 0 &&
      (target.closest('#StfForm') || target.getAttribute('form') === 'StfForm');

    if (isNative() && isStfInput) {
      if (target.type === 'checkbox') {
        // Zwilling (Chip-Panel <-> Drawer) angleichen — Filter-Inputs sind
        // über den name-Prefix "filter." eindeutig identifizierbar.
        var name = target.name;
        var value = target.value;
        document.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach(function (twin) {
          if (twin === target) return;
          if (twin.name !== name || twin.value !== value) return;
          twin.checked = target.checked;
          var twinWrap = twin.closest('.stf-check, .stf-swatch');
          if (twinWrap) twinWrap.classList.toggle('is-checked', twin.checked);
        });
      }
      if (target.type === 'number') {
        document.querySelectorAll('input[type="number"][name^="filter."]').forEach(function (twin) {
          if (twin === target) return;
          if (twin.name !== target.name) return;
          twin.value = target.value;
        });
      }
      scheduleNativeUpdate();
    }
  });

  window.addEventListener('popstate', function (event) {
    if (!isNative() || !root()) return;
    var params =
      event.state && typeof event.state.stfParams === 'string'
        ? event.state.stfParams
        : window.location.search.replace(/^\?/, '');
    nativeRender(params, false);
  });

  // Grid-App-Modus: nach jedem Re-Render offene Zustände wiederherstellen
  document.addEventListener('collection-grid-app-loaded', function () {
    restoreOpenPanel();
    updateScrollHint();
  });

  window.matchMedia(DESKTOP_MQ).addEventListener('change', function () {
    // Beim Breakpoint-Wechsel Panel zwischen Popover (im Facet) und
    // Bottom Sheet (im Portal) umziehen
    restoreOpenPanel();
  });

  /* --------------------- Scroll-Hinweis der Chip-Leiste ------------------- */

  function updateScrollHint() {
    var r = root();
    if (!r) return;
    var bar = r.querySelector('.stf-bar');
    var scroller = r.querySelector('.stf-bar__scroll');
    if (!bar || !scroller) return;
    var maxScroll = scroller.scrollWidth - scroller.clientWidth;
    bar.classList.toggle('is-scrolled', scroller.scrollLeft > 4);
    bar.classList.toggle('is-scroll-end', maxScroll <= 4 || scroller.scrollLeft >= maxScroll - 4);
  }

  function fillCountButtons() {
    var count = document.getElementById('ProductCount');
    if (!count) return;
    var text = count.textContent.trim();
    if (!text) return;
    document.querySelectorAll('[data-stf-count-btn]').forEach(function (el) {
      el.textContent = text + ' anzeigen';
    });
    document.querySelectorAll('[data-stf-count-mirror]').forEach(function (el) {
      el.textContent = text;
    });
  }

  /* ----------------------- Sticky-Zustand (mobil) -------------------------- */
  /* sterntaler-base.css macht #main-collection-filters/#main-search-filters
     mobil sticky; die Klasse .is-sticky (Schatten, Pills ausblenden) setzte
     frueher das alte Facets-Snippet — hier uebernommen. */

  function initStickyWatch() {
    var el =
      document.getElementById('main-collection-filters') ||
      document.getElementById('main-search-filters');
    if (!el) return;
    var mq = window.matchMedia('(max-width: 749px)');

    function onScroll() {
      if (!mq.matches) {
        el.classList.remove('is-sticky');
        return;
      }
      var stickyTop = parseInt(window.getComputedStyle(el).top, 10) || 0;
      var currentTop = el.getBoundingClientRect().top;
      el.classList.toggle('is-sticky', currentTop <= stickyTop);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    mq.addEventListener('change', onScroll);
    onScroll();
  }

  /* Fix: hartcodierter top:70px in sterntaler-base.css passt auf echten
     Mobilgeraeten (iOS UND Android) nicht zuverlaessig zur tatsaechlich
     sichtbaren Header-Hoehe - vermutlich wegen der ein-/ausblendenden
     Adressleiste, die die Viewport-Hoehe laufend veraendert (im
     Desktop-Mobile-Emulator gibt es das nicht, dort tritt der Bug auch
     nicht auf). Deshalb hier den echten, aktuell gerenderten Header-Rand
     laufend messen und als CSS-Variable an die Filterleiste geben, statt
     einen fixen Wert anzunehmen.
     sterntaler-base.css nutzt dafuer: top: var(--stf-sticky-top, 70px); */
  function initStickyOffsetSync() {
    var el =
      document.getElementById('main-collection-filters') ||
      document.getElementById('main-search-filters');
    var header = document.querySelector('.section-header');
    if (!el || !header) return;
    var mq = window.matchMedia('(max-width: 749px)');
    var ticking = false;

    function update() {
      ticking = false;
      if (!mq.matches) {
        el.style.removeProperty('--stf-sticky-top');
        return;
      }
      var offset = Math.max(0, header.getBoundingClientRect().bottom);
      el.style.setProperty('--stf-sticky-top', offset + 'px');
    }

    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', requestUpdate, { passive: true });
      window.visualViewport.addEventListener('scroll', requestUpdate, { passive: true });
    }
    mq.addEventListener('change', update);
    update();
  }

  function init() {
    if (!root()) return;
    ensurePortal();
    fillCountButtons();
    updateScrollHint();
    initStickyWatch();
    initStickyOffsetSync();
    var scroller = root().querySelector('.stf-bar__scroll');
    if (scroller) {
      scroller.addEventListener('scroll', updateScrollHint, { passive: true });
    }
    window.addEventListener('resize', updateScrollHint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();